import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { convexVue } from 'convex-vue'
import App from './App.vue'
import './style.css'
import router from "./router";
import { gsap } from "gsap";
import { MorphSVGPlugin } from "gsap/MorphSVGPlugin";
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import * as Sentry from "@sentry/vue";
import ui from '@nuxt/ui/vue-plugin'
import { isAndroidTablet, isIPadDevice } from './utils';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';

const app = createApp(App)

gsap.registerPlugin(MorphSVGPlugin);
gsap.registerPlugin(ScrollTrigger)

// Add Convex plugin
app.use(convexVue, {
  url: import.meta.env.VITE_CONVEX_URL
})

Sentry.init({
  app,
  dsn: "https://80b07c262f8853f18cc9215ff3a9e9eb@o4509390679572480.ingest.us.sentry.io/4509390688813056",
  integrations: [
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
  ],
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true
});

const pinia = createPinia()
app.use(router);
app.use(ui);
app.use(pinia);

app.config.errorHandler = (err, instance, info) => {
  console.error('Vue Error:', err);
  console.log('instance', instance);
  console.error('Error info:', info);
};

// On iPad the logical viewport is far wider than a phone's, so this phone-first,
// fit-exactly layout (body/#main are 100dvw x 100dvh, no scroll) reads as small.
// A scale WITHOUT width=device-width makes iOS derive the layout viewport as
// screenWidth / SCALE — the app fills that smaller canvas and the browser scales
// it up to the screen: uniform magnification, no crop. (width=device-width would
// pin full width and the scaled layout would overflow.) Applied before mount so
// Vue's first render is already at the right scale; the async detection + reflow
// are hidden behind the native splash (lifted in the mount block below).
async function scaleViewportForTablets() {
  const IPAD_SCALE = 1.5;
  try {
    if (!(await isIPadDevice()) && !(await isAndroidTablet())) return;
    document.querySelector('meta[name="viewport"]')?.setAttribute('content',
      `initial-scale=${IPAD_SCALE}, minimum-scale=${IPAD_SCALE}, maximum-scale=${IPAD_SCALE}, user-scalable=no, viewport-fit=cover`);
  } catch {
    // Device bridge unavailable — leave the default viewport in place.
  }
}

scaleViewportForTablets().finally(() => {
  app.mount('#app');
  // The native splash (launchAutoHide:false) has covered the async iPad
  // detection and viewport reflow. Reveal only after the scaled first frame has
  // painted, so the splash lifts onto the final layout with no visible jump.
  if (Capacitor.isNativePlatform()) {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => SplashScreen.hide({ fadeOutDuration: 200 })),
    );
  }
});
